import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import PublicNavbar from '../../components/common/PublicNavbar';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── SVG Icons ──────────────────────────────────────────────────────────────────
function IconCheck({ className = 'w-3.5 h-3.5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconChevron({ className = 'w-5 h-5', open }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function IconArrow({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
function IconShield({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function IconRefresh({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
    </svg>
  );
}
function IconAward({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  );
}
function IconStar({ className = 'w-4 h-4' }) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
}
function IconQuote({ className = 'w-4 h-4' }) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/></svg>;
}
function IconSparkle({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────
const FREE_FEATURES = [
  '100 free questions (Chapter 1)',
  'Multiple choice questions',
  'Basic AI feedback included',
  'Browse all 150+ roles',
  '1 JD Prep session free',
  '1 ATS CV scan included',
  'Browse all study guides (Chapter 1)',
];

const STARTER_FEATURES = [
  'All 1,000 questions for 1 role',
  'MCQ, written & spoken practice',
  'Full AI scoring on every answer',
  'End-of-session improvement plan',
  'Full access to 1 complete study guide',
  'Digital completion certificate',
  '5 JD Prep sessions/month',
  '2 ATS CV scans/month',
];

const FULL_FEATURES = [
  '1,000 questions for up to 5 roles',
  'MCQ, written & spoken practice',
  'Full AI scoring on every answer',
  'End-of-session improvement plan',
  'Access to 5 complete study guides',
  'Digital completion certificate',
  '5 JD Prep sessions/month',
  '2 ATS CV scans/month',
  'Priority access to new roles & study guides',
];

const JOB_SEEKER_FEATURES = [
  'All 1,000 questions — every role',
  'MCQ, written & spoken practice',
  'Full AI scoring on every answer',
  'End-of-session improvement plan',
  'Full access to all study guides',
  'Digital completion certificate',
  '15 JD Prep sessions/month',
  '5 ATS CV scans/month',
  'Salary Negotiation (script + AI roleplay)',
  'STAR Coach, Cover Letter & Company Prep AI tools',
];

const COMPARE_ROWS = [
  ['Questions access',          '100 (Ch.1)', '1,000 (1 role)', '5 roles',         'All roles'    ],
  ['MCQ practice',              true,          true,             true,              true           ],
  ['Written answer practice',   false,         true,             true,              true           ],
  ['Spoken answer practice',    false,         true,             true,              true           ],
  ['AI scoring & feedback',     'Basic',       'Full',           'Full',            'Full'         ],
  ['Session improvement plan',  false,         true,             true,              true           ],
  ['ATS CV scans',              '1 scan',      '2/month',        '2/month',         '5/month'      ],
  ['JD Prep sessions',          '1 free',      '5/month',        '5/month',         '15/month'     ],
  ['Study guide access',        'Ch.1 only',   '1 full guide',   '5 guides',        'All'          ],
  ['Completion certificate',    false,         true,             true,              true           ],
  ['Salary Negotiation',        false,         false,            false,             true           ],
  ['STAR Coach / Cover Letter', false,         false,            false,             true           ],
  ['Company Prep AI tool',      false,         false,            false,             true           ],
  ['All future roles',          false,         false,            false,             true           ],
  ['Access period',             '—',           '30 days',        '30 days',         '30 days'      ],
];

const PRICING_TESTIMONIALS = [
  { name: 'Lina Haddad',      role: 'Business Analyst, du Telecom',  initials: 'LH', color: '#1B3A5C',
    text: 'Bought Full Access the day before my final round. Spent the evening on spoken answers — the feedback on my filler words alone changed how I carried myself in the room.' },
  { name: 'Rami Khalil',      role: 'Finance Manager candidate',     initials: 'RK', color: '#059669',
    text: 'The value is ridiculous for $20. Three practice sessions and I already sound completely different answering "walk me through your experience".' },
  { name: 'Aisha Osei',       role: 'HR Coordinator, Aldar',         initials: 'AO', color: '#7C3AED',
    text: 'I was skeptical about AI feedback but it flagged that I kept hedging with "I think" and "maybe" — my manager noticed the change in my next performance review presentation.' },
];

const PROOF_STATS = [
  { value: '10,000+', label: 'Practice sessions' },
  { value: '150+',    label: 'Job roles covered' },
  { value: '4.9 / 5', label: 'Candidate rating' },
  { value: '125',     label: 'Questions per JD Prep' },
  { value: '92%',     label: 'Report feeling more confident' },
];

function getFaqs(p) {
  const fmt = n => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
  return [
    {
      q: 'What is the difference between the paid plans?',
      a: `Starter (${fmt(p.starter_monthly)}/month or ${fmt(p.starter_annual)}/year) unlocks 1 role + 1 complete study guide, plus 5 JD Prep sessions/month and 2 ATS CV scans/month. Full Access (${fmt(p.fa_monthly)}/month or ${fmt(p.fa_annual)}/year) unlocks up to 5 roles + 5 study guides, also with 5 JD Prep sessions/month and 2 ATS CV scans/month. Job Seeker (${fmt(p.js_monthly)}/month or ${fmt(p.js_annual)}/year) unlocks all roles, all study guides, 15 JD Prep sessions/month, and 5 ATS CV scans/month. Annual billing saves 20%.`,
    },
    {
      q: 'Can I subscribe to multiple roles on the Starter plan?',
      a: `Yes — each Starter subscription unlocks one role. If you need two roles, you can purchase two Starter subscriptions, or upgrade to Full Access (${fmt(p.fa_monthly)}/month) to unlock up to 5 roles. For unlimited roles, Job Seeker is the best value.`,
    },
    {
      q: 'What happens when my subscription expires?',
      a: "You keep access until the period ends. After that, you revert to 100 free questions per role (Chapter 1 only). Any progress and certificates you've earned are kept forever.",
    },
    {
      q: 'Can I use a coupon code?',
      a: 'Yes. Enter your coupon code on the checkout page. Coupons can be percentage-based or fixed-amount discounts.',
    },
    {
      q: 'What payment methods are accepted?',
      a: 'We accept all major credit and debit cards through our Stripe checkout. Apple Pay and Google Pay are also supported on compatible devices.',
    },
    {
      q: 'Is there a free trial?',
      a: "Every role includes 100 free questions (the full Chapter 1 — all 4 sub-chapters, 25 questions each) with AI feedback, at no cost and without a credit card. Try before you subscribe.",
    },
    {
      q: 'What is JD Prep and how does it work?',
      a: 'JD Prep generates 125 interview questions tailored to a specific job description you paste in — covering Technical Skills, Core Responsibilities, Behavioural, Situational, and Culture & Fit chapters (25 questions each). Free accounts get 1 session. Starter and Full Access subscribers get 5 sessions per rolling 30 days. Job Seeker subscribers get 15 sessions per rolling 30 days. Additional sessions beyond your allowance are available at $9.99 each.',
    },
    {
      q: 'Who is the Job Seeker plan for?',
      a: `Job Seeker (${fmt(p.js_monthly)}/month) is designed for candidates actively applying for multiple roles simultaneously — you get 15 JD Prep sessions per 30 days (3× more than Starter or Full Access) so you can generate tailored question sets for every application. You also get 30 days of access per subscription, giving you extra runway across a longer job search.`,
    },
    {
      q: 'Can I get a refund?',
      a: "Yes. If you're not satisfied, contact support within 7 days of purchase for a full refund. No questions asked.",
    },
  ];
}

// ── Components ────────────────────────────────────────────────────────────────
function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b" style={{ borderColor: '#E2E8F0' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex justify-between items-center py-5 bg-transparent border-0 cursor-pointer text-left gap-4"
        style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
      >
        <span className="font-semibold leading-[1.4]" style={{ fontSize: 16, color: '#0F172A' }}>{q}</span>
        <IconChevron className="w-5 h-5 flex-shrink-0" open={open} style={{ color: '#64748B' }} />
      </button>
      <div style={{ maxHeight: open ? '500px' : '0', overflow: 'hidden', transition: 'max-height 0.32s ease' }}>
        <p className="pb-5 pr-8 leading-[1.75]" style={{ fontSize: 15, color: '#64748B' }}>{a}</p>
      </div>
    </div>
  );
}

function CellValue({ v }) {
  if (v === true)  return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: '#D1FAE5' }}><IconCheck className="w-3 h-3" style={{ color: '#059669' }} /></span>;
  if (v === false) return <span style={{ color: '#CBD5E1', fontSize: 18, lineHeight: 1 }}>—</span>;
  return <span className="font-semibold" style={{ fontSize: 13, color: '#64748B' }}>{v}</span>;
}

function FeatureRow({ f, highlight }) {
  return (
    <div className="flex gap-2.5" style={{ fontSize: 14, color: highlight ? 'rgba(255,255,255,0.85)' : '#334155' }}>
      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: highlight ? 'rgba(193,39,45,0.35)' : '#D1FAE5' }}>
        <IconCheck className="w-2.5 h-2.5" style={{ color: highlight ? '#fca5a5' : '#059669' }} />
      </div>
      {f}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PricingPage() {
  const [billing, setBilling] = useState('monthly');
  const isAnnual = billing === 'annual';
  const [hoverCard, setHoverCard] = useState(null);

  const [couponCode, setCouponCode]     = useState('');
  const [couponInput, setCouponInput]   = useState('');
  const [couponData, setCouponData]     = useState(null);
  const [couponError, setCouponError]   = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponOpen, setCouponOpen]     = useState(false);

  async function handleApplyCoupon() {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponData(null);
    try {
      const r = await fetch(`${BASE}/system/coupon-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponInput.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { setCouponError(d.error || 'Invalid coupon'); return; }
      setCouponData(d);
      setCouponCode(couponInput.trim().toUpperCase());
    } catch {
      setCouponError('Could not validate coupon. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  }

  function removeCoupon() {
    setCouponData(null);
    setCouponCode('');
    setCouponInput('');
    setCouponError('');
  }

  // Get display price with coupon applied
  function planPrice(key) {
    if (couponData?.plans?.[key]?.applicable) return couponData.plans[key].final;
    return null; // no override
  }

  const [prices, setPrices] = useState({
    starter_monthly: 10.00, starter_annual: 96.00,
    fa_monthly: 19.99,      fa_annual: 191.90,
    js_monthly: 34.99,      js_annual: 335.90,
  });

  useEffect(() => {
    fetch(`${BASE}/system/pricing`)
      .then(r => r.json())
      .then(d => setPrices({
        starter_monthly: d.starter_monthly_price_usd,
        starter_annual:  d.starter_annual_price_usd,
        fa_monthly:      d.monthly_price_usd,
        fa_annual:       d.annual_price_usd,
        js_monthly:      d.job_seeker_monthly_price_usd,
        js_annual:       d.job_seeker_annual_price_usd,
      }))
      .catch(() => {});
  }, []);

  const fmt = (n) => Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: '#0F172A', background: '#FFFFFF', minHeight: '100vh' }}>
      <PageMeta
        title="Plans &amp; Pricing"
        description={`Simple transparent pricing. Free tier with 100 questions (Chapter 1) and 1 JD Prep session. Starter at $${prices.starter_monthly}/month for 1 role + 1 study guide. Full Access at $${prices.fa_monthly}/month for 5 roles + 5 study guides. Job Seeker at $${prices.js_monthly}/month for all roles, all study guides, and 15 JD Prep sessions. Cancel anytime.`}
        path="/pricing"
      />
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { transition-duration: 0.01ms !important; }
        }
      `}</style>

      <PublicNavbar forceLight />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="text-center px-6 py-16 md:py-20 max-w-[700px] mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border"
          style={{ background: '#F0F6FF', borderColor: '#DBEAFE' }}>
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#059669' }} />
          <span className="font-semibold" style={{ fontSize: 13, color: '#1B3A5C' }}>Simple, transparent pricing</span>
        </div>
        <h1 className="font-extrabold tracking-[-0.025em] mb-4 leading-[1.1]" style={{ fontSize: 'clamp(36px, 5vw, 52px)', color: '#0F172A' }}>
          Pick the plan that fits your goal
        </h1>
        <p className="leading-[1.7]" style={{ fontSize: 17, color: '#64748B' }}>
          Start free, go deeper with Starter, unlock 5 roles + 5 study guides with Full Access, or get everything unlimited with Job Seeker.
        </p>
        <p className="mt-4 font-semibold" style={{ fontSize: 13, color: '#94A3B8' }}>
          Join 10,000+ candidates who've completed sessions
        </p>
      </div>

      {/* ── Billing toggle ────────────────────────────────────────────────── */}
      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center gap-1 rounded-full p-1" style={{ background: '#F1F5F9', border: '1px solid #E2E8F0' }}>
          <button onClick={() => setBilling('monthly')} className="rounded-full font-semibold transition-all duration-200" style={{ fontSize: 14, padding: '9px 22px', background: !isAnnual ? '#1B3A5C' : 'transparent', color: !isAnnual ? '#fff' : '#64748B', border: 'none', cursor: 'pointer', boxShadow: !isAnnual ? '0 2px 8px rgba(27,58,92,0.25)' : 'none' }}>
            Monthly
          </button>
          <button onClick={() => setBilling('annual')} className="rounded-full font-semibold transition-all duration-200 flex items-center gap-2" style={{ fontSize: 14, padding: '9px 22px', background: isAnnual ? '#1B3A5C' : 'transparent', color: isAnnual ? '#fff' : '#64748B', border: 'none', cursor: 'pointer', boxShadow: isAnnual ? '0 2px 8px rgba(27,58,92,0.25)' : 'none' }}>
            Annual
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#FECACA', color: '#C1272D', transition: 'transform 0.2s ease', transform: isAnnual ? 'scale(1.05)' : 'scale(1)' }}>SAVE 20%</span>
          </button>
        </div>
      </div>

      {/* ── Coupon code ───────────────────────────────────────────────────── */}
      <div className="flex flex-col items-center mb-10">
        {!couponOpen && !couponData && (
          <button onClick={() => setCouponOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#64748B', fontFamily: 'inherit', textDecoration: 'underline', padding: 0 }}>
            Have a coupon code?
          </button>
        )}
        {(couponOpen && !couponData) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <input
              type="text"
              value={couponInput}
              onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
              placeholder="Enter coupon code"
              style={{ padding: '10px 14px', border: '1.5px solid #CBD5E1', borderRadius: 9, fontSize: 14, fontFamily: 'inherit', color: '#0F172A', background: '#fff', outline: 'none', width: 200, letterSpacing: '0.05em', fontWeight: 600 }}
            />
            <button onClick={handleApplyCoupon} disabled={couponLoading || !couponInput.trim()}
              style={{ padding: '10px 20px', background: '#1B3A5C', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: couponLoading || !couponInput.trim() ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: couponLoading || !couponInput.trim() ? 0.6 : 1 }}>
              {couponLoading ? 'Checking…' : 'Apply'}
            </button>
            <button onClick={() => { setCouponOpen(false); setCouponError(''); setCouponInput(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#94A3B8', fontFamily: 'inherit', padding: '10px 4px' }}>
              Cancel
            </button>
          </div>
        )}
        {couponError && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#C1272D', fontWeight: 600 }}>{couponError}</p>
        )}
        {couponData && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', background: '#D1FAE5', border: '1.5px solid #6EE7B7', borderRadius: 12 }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#065F46' }}>
              Coupon <strong>{couponCode}</strong> applied — {couponData.type === 'percent' ? `${couponData.amount}% off` : `$${parseFloat(couponData.amount).toFixed(2)} off`}
            </span>
            <button onClick={removeCoupon} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#064E3B', fontFamily: 'inherit', textDecoration: 'underline', padding: 0 }}>Remove</button>
          </div>
        )}
      </div>

      {/* ── Plan cards ────────────────────────────────────────────────────── */}
      <div className="max-w-[1300px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14 items-stretch">

        {/* Free */}
        <div className="border rounded-[20px] p-8 flex flex-col"
          style={{ background: '#FFFFFF', borderColor: hoverCard === 'free' ? '#CBD5E1' : '#E2E8F0', boxShadow: hoverCard === 'free' ? '0 8px 32px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)', transform: hoverCard === 'free' ? 'translateY(-4px)' : 'translateY(0)', transition: 'all 0.22s ease', cursor: 'default' }}
          onMouseEnter={() => setHoverCard('free')} onMouseLeave={() => setHoverCard(null)}>
          <p className="font-bold uppercase tracking-[0.1em] mb-3" style={{ fontSize: 11, color: '#64748B' }}>Free</p>
          <div className="flex items-end gap-1 mb-1.5">
            <span className="font-extrabold leading-none" style={{ fontSize: 48, color: '#0F172A' }}>$0</span>
          </div>
          <p className="mb-7" style={{ fontSize: 14, color: '#64748B' }}>Forever free. No card needed.</p>
          <Link to="/roles"
            className="block text-center py-3 rounded-xl font-bold no-underline mb-7 transition-all duration-200 cursor-pointer border-[1.5px]"
            style={{ borderColor: '#1B3A5C', color: '#1B3A5C', fontSize: 15 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1B3A5C'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#1B3A5C'; }}>
            Start free
          </Link>
          <div className="flex-1 space-y-3">
            {FREE_FEATURES.map(f => <FeatureRow key={f} f={f} highlight={false} />)}
          </div>
        </div>

        {/* Starter */}
        <div className="border-2 rounded-[20px] p-8 flex flex-col"
          style={{ background: '#FFFFFF', borderColor: '#1B3A5C', boxShadow: hoverCard === 'starter' ? '0 16px 48px rgba(27,58,92,0.18)' : '0 8px 32px rgba(27,58,92,0.10)', transform: hoverCard === 'starter' ? 'translateY(-4px)' : 'translateY(0)', transition: 'all 0.22s ease', cursor: 'default' }}
          onMouseEnter={() => setHoverCard('starter')} onMouseLeave={() => setHoverCard(null)}>
          <p className="font-bold uppercase tracking-[0.1em] mb-3" style={{ fontSize: 11, color: '#1B3A5C' }}>Starter</p>
          <div className="flex items-end gap-1.5 mb-1.5">
            {(() => {
              const key = isAnnual ? 'starter_annual' : 'starter';
              const disc = planPrice(key);
              const base = isAnnual ? prices.starter_annual : prices.starter_monthly;
              return disc !== null ? (
                <><span className="font-extrabold leading-none" style={{ fontSize: 48, color: '#059669' }}>{fmt(disc)}</span>
                <span className="pb-2 line-through" style={{ fontSize: 18, color: '#94A3B8', marginLeft: 4 }}>{fmt(base)}</span></>
              ) : (
                <span className="font-extrabold leading-none" style={{ fontSize: 48, color: '#0F172A' }}>{fmt(base)}</span>
              );
            })()}
            <span className="pb-2" style={{ fontSize: 16, color: '#64748B' }}>{isAnnual ? '/year' : '/month'}</span>
          </div>
          <p className={isAnnual ? 'mb-2' : 'mb-7'} style={{ fontSize: 14, color: '#64748B' }}>1 role + 1 study guide · {isAnnual ? '365-day' : '30-day'} access</p>
          {isAnnual && <p className="mb-5" style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Save $24 vs monthly · $8/mo billed annually</p>}
          <Link to={couponCode ? `/roles?coupon=${couponCode}` : '/roles'}
            className="block text-center text-white rounded-xl py-3 font-bold no-underline mb-7 transition-all duration-200 cursor-pointer"
            style={{ background: '#1B3A5C', fontSize: 15 }}
            onMouseEnter={e => e.currentTarget.style.background = '#142d47'}
            onMouseLeave={e => e.currentTarget.style.background = '#1B3A5C'}>
            Choose a role →
          </Link>
          <div className="flex-1 space-y-3">
            {STARTER_FEATURES.map(f => <FeatureRow key={f} f={f} highlight={false} />)}
          </div>
        </div>

        {/* Full Access */}
        <div className="rounded-[20px] p-8 flex flex-col relative overflow-hidden"
          style={{ background: '#1B3A5C', boxShadow: hoverCard === 'full' ? '0 24px 64px rgba(27,58,92,0.38)' : '0 16px 48px rgba(27,58,92,0.25)', transform: hoverCard === 'full' ? 'translateY(-4px)' : 'translateY(0)', transition: 'all 0.22s ease', cursor: 'default' }}
          onMouseEnter={() => setHoverCard('full')} onMouseLeave={() => setHoverCard(null)}>
          <div className="absolute top-4 right-4 text-white text-[11px] font-bold px-3 py-1 rounded-full" style={{ background: '#C1272D' }}>
            Most popular
          </div>
          <p className="font-bold uppercase tracking-[0.1em] mb-3" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Full Access</p>
          <div className="flex items-end gap-1.5 mb-1.5">
            {(() => {
              const key = isAnnual ? 'full_access_annual' : 'full_access';
              const disc = planPrice(key);
              const base = isAnnual ? prices.fa_annual : prices.fa_monthly;
              return disc !== null ? (
                <><span className="font-extrabold leading-none" style={{ fontSize: 42, color: '#6EE7B7' }}>{fmt(disc)}</span>
                <span className="pb-2 line-through" style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>{fmt(base)}</span></>
              ) : (
                <span className="font-extrabold text-white leading-none" style={{ fontSize: 42 }}>{fmt(base)}</span>
              );
            })()}
            <span className="pb-2" style={{ fontSize: 15, color: 'rgba(255,255,255,0.50)' }}>{isAnnual ? '/year' : '/month'}</span>
          </div>
          <p className={isAnnual ? 'mb-2' : 'mb-7'} style={{ fontSize: 14, color: 'rgba(255,255,255,0.50)' }}>5 roles + 5 study guides · {isAnnual ? '365-day' : '30-day'} access</p>
          {isAnnual && <p className="mb-5" style={{ fontSize: 12, color: '#6EE7B7', fontWeight: 600 }}>Save $47.98 vs monthly · $15.99/mo billed annually</p>}
          <Link to={`/subscribe/full-access${couponCode ? `?coupon=${couponCode}` : ''}`}
            className="block text-center py-3 text-white rounded-xl font-bold no-underline mb-7 transition-all duration-200 cursor-pointer"
            style={{ background: '#C1272D', fontSize: 15 }}
            onMouseEnter={e => e.currentTarget.style.background = '#A8202B'}
            onMouseLeave={e => e.currentTarget.style.background = '#C1272D'}>
            Get Full Access →
          </Link>
          <div className="flex-1 space-y-3">
            {FULL_FEATURES.map(f => <FeatureRow key={f} f={f} highlight />)}
          </div>
        </div>

        {/* Job Seeker */}
        <div className="rounded-[20px] p-8 flex flex-col relative overflow-hidden border-2"
          style={{ background: 'linear-gradient(160deg, #0a1a2e 0%, #0d2238 100%)', borderColor: '#C1272D', boxShadow: hoverCard === 'js' ? '0 24px 64px rgba(193,39,45,0.30)' : '0 16px 48px rgba(193,39,45,0.18)', transform: hoverCard === 'js' ? 'translateY(-4px)' : 'translateY(0)', transition: 'all 0.22s ease', cursor: 'default' }}
          onMouseEnter={() => setHoverCard('js')} onMouseLeave={() => setHoverCard(null)}>
          <div className="absolute top-4 right-4 text-white text-[11px] font-bold px-3 py-1 rounded-full" style={{ background: '#C1272D' }}>
            Best value
          </div>
          <p className="font-bold uppercase tracking-[0.1em] mb-3" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Job Seeker</p>
          <div className="flex items-end gap-1.5 mb-1.5">
            {(() => {
              const key = isAnnual ? 'job_seeker_annual' : 'job_seeker';
              const disc = planPrice(key);
              const base = isAnnual ? prices.js_annual : prices.js_monthly;
              return disc !== null ? (
                <><span className="font-extrabold leading-none" style={{ fontSize: 42, color: '#6EE7B7' }}>{fmt(disc)}</span>
                <span className="pb-2 line-through" style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>{fmt(base)}</span></>
              ) : (
                <span className="font-extrabold text-white leading-none" style={{ fontSize: 42 }}>{fmt(base)}</span>
              );
            })()}
            <span className="pb-2" style={{ fontSize: 15, color: 'rgba(255,255,255,0.50)' }}>{isAnnual ? '/year' : '/month'}</span>
          </div>
          <p className={isAnnual ? 'mb-2' : 'mb-7'} style={{ fontSize: 14, color: 'rgba(255,255,255,0.50)' }}>All roles + 15 JD Prep/month · {isAnnual ? '365-day' : '30-day'} access</p>
          {isAnnual && <p className="mb-5" style={{ fontSize: 12, color: '#6EE7B7', fontWeight: 600 }}>Save $83.98 vs monthly · $27.99/mo billed annually</p>}
          <Link to={`/subscribe/job-seeker${couponCode ? `?coupon=${couponCode}` : ''}`}
            className="block text-center py-3 text-white rounded-xl font-bold no-underline mb-7 transition-all duration-200 cursor-pointer"
            style={{ background: '#C1272D', fontSize: 15 }}
            onMouseEnter={e => e.currentTarget.style.background = '#A8202B'}
            onMouseLeave={e => e.currentTarget.style.background = '#C1272D'}>
            Get Job Seeker →
          </Link>
          <div className="flex-1 space-y-3">
            {JOB_SEEKER_FEATURES.map(f => <FeatureRow key={f} f={f} highlight />)}
          </div>
        </div>
      </div>

      {/* ── JD Prep callout ───────────────────────────────────────────────── */}
      <div className="max-w-[1040px] mx-auto px-6 mb-14">
        <div className="rounded-[20px] overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d2238 0%, #1B3A5C 100%)', boxShadow: '0 8px 32px rgba(27,58,92,0.18)' }}>
          <div className="flex flex-wrap gap-6 items-center justify-between p-8" style={{ position: 'relative', overflow: 'hidden' }}>
            {/* Background texture */}
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '36px 36px', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', right: 0, top: 0, width: 240, height: 240, background: 'radial-gradient(ellipse at right top, rgba(193,39,45,0.15) 0%, transparent 65%)', pointerEvents: 'none' }} />

            {/* Left copy */}
            <div className="flex-1" style={{ minWidth: 240, position: 'relative', zIndex: 1 }}>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full" style={{ background: 'rgba(193,39,45,0.15)', border: '1px solid rgba(193,39,45,0.3)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#C1272D', display: 'inline-block', flexShrink: 0 }} />
                <span className="font-bold uppercase tracking-wider" style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>Also available on all plans</span>
              </div>
              <h3 className="font-extrabold tracking-tight text-white mb-2" style={{ fontSize: 'clamp(18px, 2.5vw, 24px)', lineHeight: 1.2 }}>
                JD Prep — tailored to the exact job you're applying for
              </h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: 0, maxWidth: 480 }}>
                Paste any job description. Get 125 interview questions across 5 chapters in 90 seconds — MCQ, written, and spoken, all with model answers and prep tips.
              </p>
            </div>

            {/* Right: stats + CTA */}
            <div className="flex items-center gap-4 flex-wrap" style={{ position: 'relative', zIndex: 1 }}>
              <div className="flex gap-3">
                {[
                  { v: '125', l: 'questions generated' },
                  { v: '5',   l: 'structured chapters' },
                  { v: '1',   l: 'free session' },
                ].map(({ v, l }) => (
                  <div key={l} className="text-center px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <p className="font-extrabold text-white" style={{ fontSize: 22, margin: 0, lineHeight: 1, letterSpacing: '-0.03em' }}>{v}</p>
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', margin: '3px 0 0', whiteSpace: 'nowrap' }}>{l}</p>
                  </div>
                ))}
              </div>
              <Link to="/interview-prep"
                className="inline-flex items-center gap-2 font-bold text-white no-underline rounded-xl transition-all duration-200 whitespace-nowrap"
                style={{ background: '#C1272D', fontSize: 14, padding: '12px 22px' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#A8202B'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#C1272D'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Try JD Prep free <IconArrow className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="px-8 py-3" style={{ background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', margin: 0 }}>
              1 free session on free plan · Starter &amp; Full Access: 5 sessions/month · Job Seeker: 15 sessions/month · Additional sessions at $9.99 each
            </p>
          </div>
        </div>
      </div>

      {/* ── Trust badges ──────────────────────────────────────────────────── */}
      <div className="max-w-[760px] mx-auto px-6 mb-20">
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
          {[
            { icon: <IconShield className="w-4 h-4" />,  text: 'Secure Stripe checkout'     },
            { icon: <IconRefresh className="w-4 h-4" />, text: '7-day money-back guarantee' },
            { icon: <IconAward className="w-4 h-4" />,   text: 'Certificate on completion'  },
            { icon: <IconSparkle className="w-4 h-4" />, text: 'AI-powered by Claude'        },
          ].map(({ icon, text }) => (
            <div key={text} className="flex items-center gap-2" style={{ fontSize: 14, color: '#64748B' }}>
              <span style={{ color: '#94A3B8' }}>{icon}</span>
              <span>{text}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-5">
          {['Cancel anytime', 'Instant access', '7-day money back'].map(t => (
            <span key={t} className="flex items-center gap-1.5 font-semibold" style={{ fontSize: 13, color: '#334155' }}>
              <IconCheck className="w-3 h-3" style={{ color: '#059669' }} /> {t}
            </span>
          ))}
        </div>
      </div>

      {/* ── Compare table ─────────────────────────────────────────────────── */}
      <div className="max-w-[960px] mx-auto px-6 mb-20">
        <h2 className="font-extrabold text-center tracking-[-0.025em] mb-2" style={{ fontSize: 26, color: '#0F172A' }}>Compare plans</h2>
        <p className="text-center mb-8" style={{ fontSize: 14, color: '#64748B' }}>See exactly what each plan includes</p>
        <div className="bg-white border rounded-[20px] overflow-hidden" style={{ borderColor: '#E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)' }}>
          <table className="w-full border-collapse" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th className="py-4 px-5 text-left font-semibold" style={{ fontSize: 12, color: '#64748B', background: '#F8FAFC' }}>Feature</th>
                <th className="py-4 px-3 text-center font-bold" style={{ color: '#64748B', background: '#F8FAFC' }}>Free</th>
                <th className="py-4 px-3 text-center font-bold" style={{ color: '#1B3A5C', background: '#F8FAFC' }}>Starter</th>
                <th className="py-4 px-3 text-center font-bold rounded-t-lg" style={{ color: '#fff', background: '#1B3A5C' }}>
                  Full Access
                  <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle" style={{ background: '#C1272D' }}>★</span>
                </th>
                <th className="py-4 px-3 text-center font-bold" style={{ color: '#C1272D', background: '#F8FAFC' }}>Job Seeker</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map(([feature, free, starter, full, jobSeeker], i) => {
                let s = starter, f = full, js = jobSeeker;
                if (feature === 'Access period' && isAnnual) { s = '365 days'; f = '365 days'; js = '365 days'; }
                return (
                  <tr key={String(feature)} style={{ borderTop: i !== 0 ? '1px solid #F0F6FF' : undefined }}>
                    <td className="py-3.5 px-5 font-medium" style={{ color: '#334155' }}>{feature}</td>
                    <td className="py-3.5 px-3 text-center"><CellValue v={free} /></td>
                    <td className="py-3.5 px-3 text-center"><CellValue v={s} /></td>
                    <td className="py-3.5 px-3 text-center" style={{ background: 'rgba(27,58,92,0.04)' }}><CellValue v={f} /></td>
                    <td className="py-3.5 px-3 text-center"><CellValue v={js} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Social proof ──────────────────────────────────────────────────── */}
      <div style={{ background: '#F0F6FF' }} className="py-20 px-6 mb-0">
        <div className="max-w-5xl mx-auto">
          <p className="text-center mb-2" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#2563EB' }}>What candidates say</p>
          <h2 className="font-extrabold text-center tracking-[-0.025em] mb-2" style={{ fontSize: 'clamp(22px, 3vw, 32px)', color: '#0F172A' }}>Trusted by thousands of job seekers</h2>
          <div className="flex items-center justify-center gap-1 mb-12">
            {[1,2,3,4,5].map(n => <IconStar key={n} className="w-4 h-4" style={{ color: '#D97706' }} />)}
            <span className="ml-2 font-bold" style={{ fontSize: 14, color: '#334155' }}>4.9 / 5</span>
            <span className="ml-1" style={{ fontSize: 13, color: '#94A3B8' }}>from 400+ reviews</span>
          </div>
          {/* Stats strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6 mb-14 pb-14 border-b" style={{ borderColor: '#D1D5DB' }}>
            {PROOF_STATS.map(s => (
              <div key={s.label} className="text-center">
                <p className="font-extrabold" style={{ fontSize: 28, color: '#1B3A5C', lineHeight: 1 }}>{s.value}</p>
                <p className="mt-2" style={{ fontSize: 13, color: '#64748B' }}>{s.label}</p>
              </div>
            ))}
          </div>
          {/* Testimonial cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRICING_TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white rounded-[20px] p-7 flex flex-col border" style={{ borderColor: '#E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)' }}>
                <div className="flex gap-0.5 mb-5">{[1,2,3,4,5].map(n => <IconStar key={n} className="w-3.5 h-3.5" style={{ color: '#D97706' }} />)}</div>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-5" style={{ background: '#EFF6FF' }}>
                  <IconQuote className="w-4 h-4" style={{ color: '#2563EB' }} />
                </div>
                <p className="leading-[1.8] italic flex-1 mb-7" style={{ fontSize: 15, color: '#334155' }}>"{t.text}"</p>
                <div className="flex items-center gap-3 pt-5 border-t" style={{ borderColor: '#E2E8F0' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-[13px] flex-shrink-0" style={{ background: t.color }}>{t.initials}</div>
                  <div>
                    <p className="font-bold" style={{ fontSize: 14, color: '#0F172A' }}>{t.name}</p>
                    <p className="mt-0.5" style={{ fontSize: 12, color: '#94A3B8' }}>{t.role}</p>
                  </div>
                  <span className="ml-auto text-[11px] font-bold px-2 py-1 rounded-full" style={{ background: '#D1FAE5', color: '#059669' }}>Verified</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <div className="max-w-[700px] mx-auto px-6 mb-24">
        <h2 className="font-extrabold text-center tracking-[-0.025em] mb-2" style={{ fontSize: 26, color: '#0F172A' }}>
          Frequently asked questions
        </h2>
        <p className="text-center mb-10" style={{ fontSize: 14, color: '#64748B' }}>Everything you need to know before you subscribe</p>
        {getFaqs(prices).map(faq => <FAQ key={faq.q} {...faq} />)}
      </div>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <div className="py-20 px-6 text-center"
        style={{ background: 'linear-gradient(135deg, #040C18 0%, #081524 40%, #1B3A5C 100%)' }}>
        <h2 className="font-extrabold text-white mb-3 tracking-[-0.025em]" style={{ fontSize: 'clamp(28px, 4vw, 38px)' }}>
          Start with 100 free questions (Chapter 1)
        </h2>
        <p className="leading-[1.7] mb-10" style={{ fontSize: 16, color: 'rgba(255,255,255,0.55)' }}>
          No credit card. No commitment. Just practice.
        </p>
        <Link to="/register"
          className="inline-flex items-center gap-2 text-white no-underline px-8 py-4 rounded-xl font-bold transition-all duration-200 cursor-pointer"
          style={{ background: '#C1272D', fontSize: 15 }}
          onMouseEnter={e => { e.currentTarget.style.background = '#A8202B'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(193,39,45,0.30)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#C1272D'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
          Create free account <IconArrow className="w-4 h-4" />
        </Link>
      </div>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div className="px-6 md:px-10 py-6 flex flex-col sm:flex-row justify-between items-center gap-3" style={{ background: '#080f1a' }}>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.20)' }}>© {new Date().getFullYear()} CentricQ. All rights reserved.</p>
        <div className="flex gap-6">
          <Link to="/privacy" className="no-underline transition-colors" style={{ fontSize: 13, color: 'rgba(255,255,255,0.20)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.20)'}>Privacy</Link>
          <Link to="/terms" className="no-underline transition-colors" style={{ fontSize: 13, color: 'rgba(255,255,255,0.20)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.20)'}>Terms</Link>
        </div>
      </div>
    </div>
  );
}
